import React, { useState } from 'react'
import { useFormik } from 'formik'
import Modal from '../../components/Modal'
import ModalClose from '../../components/Modal/ModalClose'
import FormInput from '../../components/Input'
import DefaultButton from '../../components/Button/Button'
import ResidentService from '../../api/resident-service'
import { showToast } from '../../utils/toast'
import { createResidentTypeSchema } from '../../validations/createResidentType'

interface AddResidentTypeProps {
  estateId: string
  onClose: () => void
  mutate: () => void
}

const AddResidentType = ({ estateId, onClose, mutate }: AddResidentTypeProps) => {
  const [isLoading, setIsLoading] = useState(false)

  const formik = useFormik({
    initialValues: {
      title: '',
      price: 0,
      description: '',
    },
    validationSchema: createResidentTypeSchema,
    onSubmit: async (values, { resetForm }) => {
      if (isLoading) return

      setIsLoading(true)
      try {
        await ResidentService.addResidentType(estateId, {
          title: values.title,
          price: values.price,
          description: values.description,
        })

        showToast({
          title: 'Success',
          description: 'Resident type added successfully.',
          status: 'success',
        })

        resetForm()
        mutate()
        onClose()
      } catch (error: any) {
        showToast({
          title: 'Error',
          description: error.message || 'Failed to add resident type.',
          status: 'error',
        })
      } finally {
        setIsLoading(false)
      }
    },
  })

  return (
    <Modal
      open
      close={onClose}
      widthClass="w-[calc(100%-32px)] sm:max-w-lg md:max-w-[776px]"
      modalClassName="p-4 sm:p-6 md:p-8 max-h-[90vh] overflow-y-auto flex flex-col"
    >
      <ModalClose onClick={onClose} />
      <div className="py-6 sm:py-10 px-4 sm:px-8">
        <form onSubmit={formik.handleSubmit}>
          <div className="flex gap-8 mb-5">
            <FormInput
              label="Resident type"
              placeholder="e.g Bungalow"
              size="lg"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              error={formik.errors.title}
            />
            <FormInput
              label="Levy fee in Naira"
              placeholder="e.g 1000"
              size="lg"
              name="price"
              type="number"
              value={formik.values.price === 0 ? '' : formik.values.price}
              onChange={(e) => {
                const value = e.target.value
                formik.setFieldValue('price', value === '' ? 0 : Number(value))
              }}
              error={formik.errors.price}
            />
          </div>
          <div className="flex flex-col gap-2 mb-5">
            <label htmlFor="description" className="text-lg font-medium">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              rows={4}
              className="border border-solid rounded-lg p-2"
              value={formik.values.description}
              onChange={formik.handleChange}
            ></textarea>
            {formik.errors.description && (
              <p className="text-red-500 text-sm">{formik.errors.description}</p>
            )}
          </div>
          <DefaultButton
            isLoading={isLoading}
            loadingText="Creating..."
            type="submit"
            className="float-right mt-5"
            disabled={isLoading || !(formik.dirty && formik.isValid)}
          >
            Add
          </DefaultButton>
        </form>
      </div>
    </Modal>
  )
}

export default AddResidentType
